import * as React from 'react';
import Link from 'next/link';
import { splitEvery } from 'rambda';
import styled from 'styled-components';
import { WidthProps, width } from 'styled-system';

import { FlyoutTileItem } from '@packages/common';
import { themeGet, Anchor, Flex, Box, ButtonWithIcon } from '@packages/ui';

import { FlyoutTiles } from './FlyoutTiles';

const FLYOUT_WIDTH = 994;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
  max-width: ${FLYOUT_WIDTH}px;
  margin: 0 calc(50vw - ${FLYOUT_WIDTH / 2}px);
  padding: 0 ${themeGet.space(3)};
`;

const CloseButtonContainer = styled(Flex)`
  width: 100%;
  margin-right: -${themeGet.space(2)};
`;

const FlyoutLinkColumnItem = styled.div`
  padding: ${themeGet.space(2)};
`;

const FlyoutContentContainer = styled.div`
  display: flex;
  flex: 1 1 0;
  justify-content: center;
`;

const FlyoutStyled = styled.div<WidthProps>`
  display: flex;
  ${width}
`;

const FlyoutAnchor = styled(Anchor)`
  font-size: ${themeGet.fontSize(6)};
  display: table-cell;
  transition: ${themeGet.transition()};
  color: ${themeGet.colors('kilamanjaro')};
  padding: 0px ${themeGet.space(1)};
  border-bottom: 2px solid transparent;
  &:hover,
  &:active {
    color: ${themeGet.colors('primary')};
    border-bottom: 2px solid ${themeGet.colors('primary')};
  }
`;

export interface FlyoutNavItem {
  title: string;
  shortTitle: string;
  url: string;
}

interface FlyoutTile extends Omit<FlyoutTileItem, 'uuid'> {}

interface FlyoutProps {
  items: FlyoutNavItem[];
  handleCloseFlyout: VoidFunction;
  tiles: FlyoutTile[];
}

export const Flyout: React.FC<FlyoutProps> = ({ handleCloseFlyout, items = [], tiles = [] }) => {
  const sliceLength = Math.ceil((items ? items.length : NaN) / 3) || 1;
  const splitted = splitEvery(sliceLength, items || []);
  return splitted.length ? (
    <Wrapper>
      <CloseButtonContainer py={1} justifyContent="flex-end">
        <ButtonWithIcon
          padding={0}
          variant="clean"
          iconColor="sandDune"
          iconName="close"
          onClick={handleCloseFlyout}
          data-testid="navigation:flyout:close-button"
        />
      </CloseButtonContainer>
      <FlyoutContentContainer>
        <FlyoutStyled width={2 / 3}>
          {splitted.map((items, index) => (
            <Box width={1 / 3} key={index}>
              {items.map((item) => (
                <FlyoutLinkColumnItem key={item.title}>
                  <Link prefetch={false} href={item.url}>
                    <FlyoutAnchor href={item.url}>{item.title}</FlyoutAnchor>
                  </Link>
                </FlyoutLinkColumnItem>
              ))}
            </Box>
          ))}
        </FlyoutStyled>
        <Box width={1 / 3}>{tiles ? <FlyoutTiles items={tiles} /> : null}</Box>
      </FlyoutContentContainer>
    </Wrapper>
  ) : null;
};
